import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
export function RelationshipOutput() {
  const {
    reportId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = searchParams.get('state') || 'preview';
  const isUnlocked = state === 'full';
  const userData = {
    personA: {
      name: 'Anish',
      dateOfBirth: '19 June 1989',
      timeOfBirth: '2:50 PM',
      placeOfBirth: 'Surat, India'
    },
    personB: {
      name: 'Partner',
      dateOfBirth: '12 March 1991',
      timeOfBirth: '9:10 AM',
      placeOfBirth: 'Mumbai, India'
    }
  };
  const previewContent = {
    section1: {
      title: '1. System A — Core Architecture (Anish)',
      subtitle: 'Architecture-first, coherence-seeking system.',
      content: `This system:

• prioritizes structure over comfort
• seeks control through understanding
• gets restless inside incoherent containers
• prefers redesign over compromise

It operates with a deep rule:

"If the structure is wrong, the relationship will eventually strain."`
    },
    section2: {
      title: '2. Interaction Axis',
      subtitle: 'Autonomy × Emotional Attunement',
      content: `This relationship is organized around a central tension:

System A moves toward:
• independence, structure, and self-direction

System B moves toward:
• emotional connection, mutual adjustment, and relational safety

This creates a repeating dynamic:

One side moves toward structure
The other moves toward closeness
Both feel slightly misunderstood by the other's default move

This is not a values problem. It is a system-orientation difference.`
    }
  };
  const fullReportContent = {
    section3: {
      title: '3. System B — Core Architecture',
      content: `This system:

• is emotionally attuned
• safety-oriented
• relationship-first
• responsive to emotional atmosphere

It operates with a deep rule:

"If the emotional field is safe, everything else can be worked out."`
    },
    section4: {
      title: '4. Friction Mechanics',
      content: `Primary friction comes from:

• A seeking autonomy and structure
• B seeking emotional synchronization

Typical loop:

A withdraws to think
B moves closer to reconnect
A feels pressured
B feels abandoned`
    },
    section5: {
      title: '5. Harmony Mechanics',
      content: `This relationship stabilizes when:

• A provides structural clarity and direction
• B provides emotional grounding and relational glue

One holds the map. The other holds the field.`
    },
    section6: {
      title: '6. Power & Safety Dynamics',
      content: `A feels powerful through structural control
B feels safe through emotional continuity

When these are threatened:

• A becomes distant
• B becomes emotionally insistent`
    },
    section7: {
      title: '7. Conflict Loop Map',
      content: `Core loop:

Distance → Pursuit → Pressure → Withdrawal → Distance

Not because of malice. Because of opposite nervous-system strategies.`
    },
    section8: {
      title: '8. Intimacy vs Distance Pattern',
      content: `A needs space to regulate
B needs closeness to regulate

The relationship must oscillate, not freeze at one pole.`
    },
    section9: {
      title: '9. Long-Range Structural Risks',
      content: `• Chronic misinterpretation of each other's regulation style
• Power struggles over "who sets the tone"
• Emotional exhaustion if not structurally understood`
    },
    section10: {
      title: '10. Relationship System Summary',
      content: `This is a complementary but tense two-system configuration.

The friction is not personal. It is architectural.`
    }
  };
  const handleUnlock = () => {
    navigate(`/report/relationship/${reportId}/unlock`);
  };
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('•')) {
        return <div key={i} className="flex items-start gap-3 ml-4" data-id="element-3283">
            <span className="text-[#64748B] mt-1.5" data-id="element-3284">•</span>
            <span data-id="element-3285">{line.substring(1).trim()}</span>
          </div>;
      }
      if (line.trim().startsWith('"') && line.trim().endsWith('"')) {
        return <blockquote key={i} className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/90 my-4" data-id="element-3286">
            {line.trim().slice(1, -1)}
          </blockquote>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-4" data-id="element-3287" />;
      }
      return <p key={i} data-id="element-3288">{line}</p>;
    });
  };
  return <>
      <Helmet data-id="element-3289">
        <title data-id="element-3290">
          Relationship Dynamics Diagnostic — Report | Lazy Monk Labs
        </title>
        <meta name="robots" content="noindex, nofollow" data-id="element-3291" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3292">
        <header data-id="element-3293">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3294">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3295">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3296">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" data-id="element-3297" />
                <span className="text-lg font-medium tracking-tight" data-id="element-3298">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-3299">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3300">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3301">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-3302">
          <div className="max-w-4xl mx-auto" data-id="element-3303">
            <section className="mb-16" data-id="element-3304">
              <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-3305">
                <div className="flex items-start justify-between mb-6" data-id="element-3306">
                  <h1 className="text-3xl md:text-4xl font-medium leading-tight tracking-tight" data-id="element-3307">
                    ❤️ Relationship Dynamics Diagnostic
                  </h1>
                  <div className={`px-3 py-1 border rounded text-xs uppercase tracking-wider flex items-center gap-2 ${isUnlocked ? 'bg-[#64748B]/20 border-[#64748B]/30 text-[#64748B]' : 'bg-[#2B2B2B]/20 border-[#2B2B2B] text-[#F5F5F5]/50'}`} data-id="element-3308">
                    <span data-id="element-3309">{isUnlocked ? '🟢' : '🟡'}</span>
                    {isUnlocked ? 'Full Diagnostic' : 'Preview'}
                  </div>
                </div>
                <div className="space-y-4 text-sm text-[#F5F5F5]/60 mb-6" data-id="element-3310">
                  <div data-id="element-3311">
                    <p className="font-medium text-[#F5F5F5]/80 mb-1" data-id="element-3312">
                      Person A: {userData.personA.name}
                    </p>
                    <p data-id="element-3313">
                      DOB: {userData.personA.dateOfBirth} —{' '}
                      {userData.personA.timeOfBirth} —{' '}
                      {userData.personA.placeOfBirth}
                    </p>
                  </div>
                  <div data-id="element-3314">
                    <p className="font-medium text-[#F5F5F5]/80 mb-1" data-id="element-3315">
                      Person B: {userData.personB.name}
                    </p>
                    <p data-id="element-3316">
                      DOB: {userData.personB.dateOfBirth} —{' '}
                      {userData.personB.timeOfBirth} —{' '}
                      {userData.personB.placeOfBirth}
                    </p>
                  </div>
                </div>
                <blockquote className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/80" data-id="element-3317">
                  This is a structural diagnostic of how two systems interact,
                  where friction is structural, and where harmony is natural.
                </blockquote>
              </div>
            </section>

            <section className="mb-16" data-id="element-3318">
              {!isUnlocked && <div className="mb-8 flex items-center gap-3" data-id="element-3319">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-3320">
                    Preview Layer
                  </div>
                </div>}

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3321">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-3322">
                  {previewContent.section1.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-3323">
                  {previewContent.section1.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-3324">
                  {formatContent(previewContent.section1.content)}
                </div>
              </div>

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3325">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-3326">
                  {previewContent.section2.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-3327">
                  {previewContent.section2.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-3328">
                  {formatContent(previewContent.section2.content)}
                </div>
              </div>

              {!isUnlocked && <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-3329">
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-3330">
                    🔒 This is the first layer of your Relationship Dynamics
                    Diagnostic.
                  </p>
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-3331">
                    The full report continues with:
                  </p>
                  <ul className="space-y-2 text-[#F5F5F5]/60 text-sm mb-6" data-id="element-3332">
                    <li data-id="element-3333">• System B's core architecture</li>
                    <li data-id="element-3334">• Friction mechanics</li>
                    <li data-id="element-3335">• Harmony mechanics</li>
                    <li data-id="element-3336">• Power & safety dynamics</li>
                    <li data-id="element-3337">• Conflict loops</li>
                    <li data-id="element-3338">• Intimacy vs distance pattern</li>
                    <li data-id="element-3339">• Long-range structural risks</li>
                    <li data-id="element-3340">• Relationship system summary</li>
                  </ul>
                </div>}
            </section>

            {!isUnlocked && <section className="mb-16" data-id="element-3341">
                <div className="border-2 border-[#64748B]/30 p-12 bg-[#111214] text-center" data-id="element-3342">
                  <Lock className="w-12 h-12 text-[#64748B] mx-auto mb-6" data-id="element-3343" />
                  <h2 className="text-3xl font-medium mb-6" data-id="element-3344">
                    Unlock Full Diagnostic
                  </h2>
                  <p className="text-lg text-[#F5F5F5]/70 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-3345">
                    This is not a different report. This is the rest of the same
                    diagnostic.
                  </p>

                  <div className="mb-12" data-id="element-3346">
                    <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-6 uppercase tracking-wider" data-id="element-3347">
                      Locked Sections
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto text-left" data-id="element-3348">
                      {['System B — Core Architecture', 'Friction Mechanics', 'Harmony Mechanics', 'Power & Safety Dynamics', 'Conflict Loop Map', 'Intimacy vs Distance Pattern', 'Long-Range Structural Risks', 'Relationship System Summary'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3349">
                          <Lock className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-3350" />
                          <span className="text-[#F5F5F5]/70 text-sm" data-id="element-3351">
                            {item}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <button onClick={handleUnlock} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3352">
                    🔓 Unlock Full Diagnostic →
                  </button>

                  <p className="mt-6 text-xs text-[#F5F5F5]/40" data-id="element-3353">
                    One-time unlock · Complete diagnostic access
                  </p>
                </div>
              </section>}

            {isUnlocked && <section id="unlocked-content" data-id="element-3354">
                <div className="mb-8 flex items-center gap-3" data-id="element-3355">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-3356">
                    Full Diagnostic
                  </div>
                </div>

                {Object.values(fullReportContent).map((section, i) => <div key={i} className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3357">
                    <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-3358">
                      {section.title}
                    </h2>
                    <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-3359">
                      {formatContent(section.content)}
                    </div>
                  </div>)}

                <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-3360">
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3361">
                    This report explains how two systems interact. It does not
                    tell you what to do, how to change, or who to become. This
                    is a diagnostic document, not a prescription.
                  </p>
                </div>
              </section>}
          </div>
        </main>

        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-3362">
          <div className="max-w-7xl mx-auto text-center" data-id="element-3363">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-3364">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}